import { useState, useEffect } from 'react'
import {
  ArrayLocationAPIResponse,
  SiteRFPSummariesSortByParam,
} from '@black-bear-energy/black-bear-energy-common'
import {
  Alert,
  Box,
  Pagination,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import { Surface } from '../components/common/Surface'
import { SitesSearchBar } from '../components/Sites/SitesSearchBar'
import { SitesTable } from '../components/Sites/SitesTable'
import { SiteRFPPhaseOption } from 'components/helpers/getSiteRFPPhaseLabel'
import {
  useSiteRFPSummaries,
  SiteActiveStatus,
} from '../api/hooks/useSiteRFPSummaries'
import {
  useImpersonatingUserOrUser,
  isEntityUser,
  isChannelPartnerUser,
} from '../state/modules/auth'
import { useQuery } from '../state/modules/common'

export function Sites() {
  const currentUser = useImpersonatingUserOrUser()
  const hidePreRFP =
    !!currentUser &&
    (isEntityUser(currentUser) || isChannelPartnerUser(currentUser))

  const query = useQuery()
  const [search, setSearch] = useState(() => query.get('search') ?? '')
  const [activeStatus, setActiveStatus] = useState<SiteActiveStatus>('active')
  const [phase, setPhase] = useState<SiteRFPPhaseOption[]>([])
  const [arrayLocation, setArrayLocation] = useState<
    ArrayLocationAPIResponse[]
  >([])
  const [sortBy, setSortBy] = useState<SiteRFPSummariesSortByParam>(
    SiteRFPSummariesSortByParam.SiteName
  )
  const [sortAsc, setSortAsc] = useState(true)
  const [page, setPage] = useState(1)

  const { data, isPending, isError, isFetching, error } = useSiteRFPSummaries(
    search,
    activeStatus,
    phase,
    arrayLocation,
    sortBy,
    sortAsc,
    page
  )

  useEffect(() => {
    if (error) {
      console.error('[Sites] Failed to fetch site RFP summaries', error)
    }
  }, [error])

  const records = data?.records ?? []
  const totalPages = data?.pagination.totalPages ?? 1
  const hasActiveFilters =
    search !== '' ||
    phase.length > 0 ||
    arrayLocation.length > 0 ||
    activeStatus !== 'active'

  function resetPage() {
    setPage(1)
  }

  function handleSortBy(newSortBy: SiteRFPSummariesSortByParam) {
    setSortBy(newSortBy)
    resetPage()
  }

  function handleSortAsc(newSortAsc: boolean) {
    setSortAsc(newSortAsc)
    resetPage()
  }

  return (
    <Box>
      <Typography variant="h4" color={rucksack.palette.text.primary}>
        Sites
      </Typography>
      {isError && !data && (
        <Alert severity="error" variant="outlined" sx={{ marginTop: 2 }}>
          Failed to load sites. Please refresh the page.
        </Alert>
      )}
      {isError && data && !isFetching && (
        <Alert severity="warning" variant="outlined" sx={{ marginTop: 2 }}>
          Could not refresh data. Showing previous results.
        </Alert>
      )}
      <Box sx={{ marginTop: '16px' }}>
        <Surface>
          <SitesSearchBar
            search={search}
            setSearch={(newSearch) => {
              setSearch(newSearch)
              resetPage()
            }}
            activeStatus={activeStatus}
            setActiveStatus={(newActiveStatus) => {
              setActiveStatus(newActiveStatus)
              resetPage()
            }}
            phase={phase}
            setPhase={(newPhase) => {
              setPhase(newPhase)
              resetPage()
            }}
            hidePreRFP={hidePreRFP}
            arrayLocation={arrayLocation}
            setArrayLocation={(newArrayLocation) => {
              setArrayLocation(newArrayLocation)
              resetPage()
            }}
          />
          <SitesTable
            records={records}
            isPending={isPending}
            isError={isError}
            sortBy={sortBy}
            setSortBy={handleSortBy}
            sortAsc={sortAsc}
            setSortAsc={handleSortAsc}
            search={search}
            hasActiveFilters={hasActiveFilters}
          />
          {records.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
              />
            </Box>
          )}
        </Surface>
      </Box>
    </Box>
  )
}
